import { ChangeEvent, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import { useIntl } from 'react-intl'
import { ColData, UfinetSimpleInput } from 'ufinet-web-components'

type TextFilterHeaderProps = {
	label: ReactNode
	resetToken: number
	liveValueRef: MutableRefObject<string>
	onDebouncedChange: (value: string) => void
}

// Shared free-text column filter: the shared Table component always evaluates column filters with the
// 'in' match mode, which breaks a plain text value, so aqs-backend's clientNameSearch/networkAreaNameSearch
// (LIKE '%term%', see EquipmentCostFinder et al.) is used instead, and the column must be marked
// nonFilterable to skip that broken path.
// The typed value is kept as local state here (instead of in the parent page) so the column definition
// built by applyToColumn below stays referentially stable while typing - otherwise the shared Table
// re-renders its columns on every keystroke and the input loses focus after each character.
const TextFilterHeader = ({ label, resetToken, liveValueRef, onDebouncedChange }: TextFilterHeaderProps) => {
	const [inputValue, setInputValue] = useState('')

	useEffect(() => {
		liveValueRef.current = ''
		setInputValue('')
	}, [resetToken, liveValueRef])

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		liveValueRef.current = e.target.value
		setInputValue(e.target.value)
		onDebouncedChange(e.target.value)
	}

	return (
		<div className="d-flex flex-column">
			<span>{label}</span>
			{/* Stop propagation so interacting with the input doesn't trigger the column's sort handler */}
			<div className="mt-2" onClick={(e) => e.stopPropagation()}>
				<UfinetSimpleInput type="text" className="w-100" value={inputValue} onChange={handleChange} />
			</div>
		</div>
	)
}

// Client-side partial, case-insensitive, trimmed match - still used by pages/loadKmz/areas (that table
// has no server-side name search), even though the costs pages moved to server-side search.
export const matchesTextFilter = (value: string | undefined | null, term: string): boolean => {
	const trimmedTerm = term.trim().toLowerCase()
	if (!trimmedTerm) return true
	return (value || '').toLowerCase().includes(trimmedTerm)
}

// Encapsulates one free-text column filter: the column override (header + nonFilterable) and a reset
// used to keep the shared Table's "clear filters" action in sync with this filter's state.
// `inputValue` (in TextFilterHeader) updates on every keystroke so typing feels responsive, but `value`
// (what pages use to build the clientNameSearch/networkAreaNameSearch/updatedBy criteria sent to the
// backend) is debounced, so a search request only fires once the user pauses typing instead of once per
// keystroke. `getValue()` returns the latest typed text immediately (no debounce); kept for API symmetry.
export const useColumnTextFilter = (labelId: string) => {
	const intl = useIntl()
	const [value, setValue] = useState('')
	const [resetToken, setResetToken] = useState(0)
	const liveValueRef = useRef('')

	const debouncedSetValue = useMemo(() => debounce(setValue, 400), [])

	// Cancel any pending debounced update when the column (and this hook instance) is torn down.
	useEffect(() => () => debouncedSetValue.cancel(), [debouncedSetValue])

	const applyToColumn = useCallback(
		(column: ColData): ColData => ({
			...column,
			nonFilterable: true,
			header: (
				<TextFilterHeader
					label={intl.formatMessage({ id: labelId })}
					resetToken={resetToken}
					liveValueRef={liveValueRef}
					onDebouncedChange={debouncedSetValue}
				/>
			),
		}),
		[labelId, intl, resetToken, debouncedSetValue]
	)

	const reset = useCallback(() => {
		debouncedSetValue.cancel()
		setResetToken((token) => token + 1)
		liveValueRef.current = ''
		setValue('')
	}, [debouncedSetValue])

	const getValue = useCallback(() => liveValueRef.current, [])

	// Stable identity across renders: only changes when the debounced value or applyToColumn actually
	// change, so consumers' useMemo(cols/filteredContent, [...filter]) don't recompute on every render.
	return useMemo(() => ({ value, applyToColumn, reset, getValue }), [value, applyToColumn, reset, getValue])
}
